"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { Clock, Mail, Phone, MessageSquare, FileText } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const hubs = [{
  code: "SHJ / 01 / HQ",
  name: "Sharjah Main Office",
  region: "NORTHERN EMIRATES",
  address: ["Industrial Area 12", "Warehouse 4", "Sharjah, UAE"],
  phone: "+971 6 555 0101",
  email: "sales.shj@worldsky.ae",
  hours: "Mon–Sat 8a–6p",
  status: "OPERATIONAL",
  manager: "Ahmed Al-Farsi",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/manager_ahmed_sharjah.png"
}, {
  code: "AJM / 02 / WH",
  name: "Ajman Distribution",
  region: "NORTHERN EMIRATES",
  address: ["New Industrial Area", "Plot 88", "Ajman, UAE"],
  phone: "+971 6 744 0202",
  email: "sales.ajm@worldsky.ae",
  hours: "Mon–Sat 8a–6p",
  status: "OPERATIONAL",
  manager: "Fatima Al-Zahra",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/manager_fatima_ajman.webp"
}, {
  code: "ALN / 03 / EXP",
  name: "Al Ain Logistics",
  region: "EASTERN REGION",
  address: ["Industrial City", "Bay 12", "Al Ain, UAE"],
  phone: "+971 3 755 0303",
  email: "sales.aln@worldsky.ae",
  hours: "Mon–Sat 8a–6p",
  status: "OPERATIONAL",
  manager: "Khalid Mansoor",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/manager_khalid_alain.png"
}, {
  code: "DXB / 04 / EXP",
  name: "Dubai Export Hub",
  region: "CENTRAL HUB",
  address: ["Jebel Ali Free Zone", "Office 202", "Dubai, UAE"],
  phone: "+971 4 888 0404",
  email: "export@worldsky.ae",
  hours: "Mon–Fri 8a–6p",
  status: "OPERATIONAL",
  manager: "Sarah Jenkins",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/manager_sarah_dubai.webp"
}];
const contactMethods = [{
  id: 1,
  title: "Email Inquiry",
  value: "info@world-sky.ae",
  link: "mailto:info@world-sky.ae",
  icon: Mail,
  label: "Send Email"
}, {
  id: 2,
  title: "WhatsApp Support",
  value: "+971 52 775 0331",
  link: "https://wa.me/971527750331",
  icon: MessageSquare,
  label: "Chat Now"
}, {
  id: 3,
  title: "Inquiry Form",
  value: "Project Specifications",
  link: "#inquiry-form",
  icon: FileText,
  label: "Open Form"
}];
export default function SalesChannels() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: pillRef,
    isVisible: pillVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: contactRef,
    isVisible: contactVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="823" id="direct-sales-channels" className="bg-slate-950 py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center justify-center gap-2 mb-6">
            <span className="inline-block size-1.5 rounded-lg bg-sky-500 animate-pulse" />
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-white">
              Regional Presence
            </span>
          </div>
          <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-[64px] leading-[1.05] tracking-tighter text-white">
            Strategic Hubs. <span className="italic font-light">Engineered Precision.</span>
          </h2>
          <p className="mt-6 font-default text-lg text-white max-w-xl mx-auto">
            Direct supply chain access across the UAE, ensuring rapid logistics for your architectural projects.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {hubs.map((hub, i) => <HubCard key={hub.code} hub={hub} index={i} dataIndex={i} />)}
        </div>

        <div ref={pillRef} data-index={0} className={`relative my-16 flex justify-center transition-all duration-700 ease-out ${pillVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-foreground/10 hidden md:block" />
          <Link to="/ConnectWithSalesPage" className="relative inline-flex items-center gap-3 rounded-lg bg-sky-500 px-8 py-4 font-default text-sm font-semibold tracking-wide uppercase text-white hover:bg-sky-600 transition-all shadow-lg">
            <span>Request a Corporate Quote</span>
            <span className="inline-block size-1.5 rounded-lg bg-white animate-pulse" />
          </Link>
        </div>

        <div ref={contactRef} className={`mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 transition-all duration-700 ease-out ${contactVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {contactMethods.map((method, i) => <div key={method.id} data-index={i} className="flex flex-col items-center text-center p-8 rounded-lg bg-slate-900 border border-slate-800 hover:border-sky-500 transition-colors group">
              <div className="mb-4 p-3 rounded-lg bg-sky-500/10 text-sky-500 group-hover:bg-sky-500 group-hover:text-white transition-all">
                <method.icon className="size-6" />
              </div>
              <h4 className="font-default font-semibold text-lg text-white mb-1">
                {method.title}
              </h4>
              <p className="font-mono text-sm text-white mb-6">
                {method.value}
              </p>
              <Link to={method.link} className="w-full inline-flex items-center justify-center rounded-lg bg-sky-500 px-6 py-3 font-default text-xs font-bold tracking-widest uppercase text-white hover:bg-sky-600 transition-all shadow-md">
                {method.label}
              </Link>
            </div>)}
        </div>
      </div>
    </section>;
}
type Hub = {
  code: string;
  name: string;
  region: string;
  address: string[];
  phone: string;
  email: string;
  hours: string;
  status: string;
  manager: string;
  avatar: string;
};
function HubCard({
  hub,
  index,
  dataIndex
}: {
  hub: Hub;
  index: number;
  dataIndex: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const initials = hub.manager.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
  const telHref = `tel:${hub.phone.replace(/[^0-9+]/g, "")}`;
  const mailHref = `mailto:${hub.email}`;
  return <div ref={ref} data-index={dataIndex} style={{
    transitionDelay: `${index * 60}ms`
  }} className={`group relative bg-slate-900 border border-slate-800 rounded-lg p-7 flex flex-col aspect-[4/5] transition-all duration-700 ease-out hover:-translate-y-0.5 hover:border-sky-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[11px] tracking-[0.15em] text-white group-hover:text-sky-500 transition-colors">
          {hub.code}
        </span>
        <span className="inline-block size-1.5 rounded-lg bg-sky-500 animate-pulse" />
      </div>

      <div className="mt-5">
        <h3 className="font-default font-semibold text-[22px] leading-tight tracking-tight text-white">
          {hub.name}
        </h3>
        <p className="mt-1.5 font-mono text-[10px] tracking-[0.18em] uppercase text-white">
          {hub.region}
        </p>
      </div>

      <div className="my-5 h-px w-full bg-slate-800" />

      <div className="space-y-4 flex-1">
        <div>
          <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-white mb-1.5">
            Location
          </p>
          <div className="font-default text-[13px] leading-relaxed text-white">
            {hub.address.map((line, i) => <div key={i} data-index={i}>
                {line}
              </div>)}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Phone className="size-3.5 text-sky-500 shrink-0" />
          <Link to={telHref} className="font-default font-medium text-[15px] text-white hover:text-sky-500 transition-colors">
            {hub.phone}
          </Link>
        </div>

        <div className="flex items-center gap-2">
          <Mail className="size-3.5 text-sky-500 shrink-0" />
          <Link to={mailHref} className="font-mono text-[11px] text-white hover:text-sky-500 transition-colors break-all">
            {hub.email}
          </Link>
        </div>

        <div className="flex items-center gap-2">
          <Clock className="size-3.5 text-sky-500 shrink-0" />
          <span className="font-mono text-[11px] text-white">
            {hub.hours}
          </span>
        </div>
      </div>

      <div className="my-5 h-px w-full bg-slate-800" />

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <SafeHydrate><Avatar className="size-8 rounded-lg border border-slate-800">
            <AvatarImage src={hub.avatar} alt={hub.manager} />
            <AvatarFallback className="rounded-lg bg-slate-800 text-white text-[10px] font-mono">
              {initials}
            </AvatarFallback>
          </Avatar></SafeHydrate>
          <span className="font-default text-[12px] text-white truncate">
            {hub.manager}
          </span>
        </div>
        <Link to={telHref} className="font-mono text-[11px] tracking-[0.1em] text-sky-500 hover:underline transition-colors whitespace-nowrap">
          Call Now →
        </Link>
      </div>
    </div>;
}